/**
 * Typed wrappers for the `/api/builder/admin/*` user + permission
 * endpoints (PR-3 builder reorg — the builder becomes the single
 * admin console).
 *
 * Faithful port of the hp-portal client (`adminUsersApi` /
 * `adminPermissionsApi` in `apps/hp-portal/frontend/src/lib/api.ts`)
 * pointed at the builder routes. Types mirror
 * `src/builder/schemas/admin_users.py`.
 */

import { api } from "@/lib/api";

// ── Fixed vocabulary (mirror of the migration-011 CHECK constraints) ──
//
// This block is one of FIVE copies of the (section, action) vocabulary,
// pinned to the other four by
// `apps/hp-portal/tests/contracts/test_permission_vocabulary_is_pinned.py`.
// The others: the portal's pydantic schema, `me.py::_ACTIONS_BY_SECTION`,
// what migrations 011 + 040 seed into `permission_role_defaults`, and
// `src/builder/schemas/admin_users.py`. Add an action to all five in the
// same PR — the test names the ones you missed.
//
// `send_report` was absent here until 2026-09-07, which is why the matrix
// below never drew its column even though migration 040 seeds the two
// role defaults for it.

export type PermissionSection = "dashboard" | "maestros" | "files";

export type PermissionAction =
  | "view"
  | "view_data"
  | "run_chat"
  | "run_ai_analysis"
  | "send_report"
  | "schedule_send"
  | "upload"
  | "download"
  | "delete";

export type AdminUserRole = "admin" | "user";

export const PERMISSION_SECTIONS: readonly PermissionSection[] = [
  "dashboard",
  "maestros",
  "files",
];

export const ACTIONS_BY_SECTION: Record<
  PermissionSection,
  readonly PermissionAction[]
> = {
  dashboard: [
    "view",
    "view_data",
    "run_chat",
    "run_ai_analysis",
    "send_report",
    "schedule_send",
  ],
  maestros: ["view", "upload", "download", "delete"],
  files: ["view", "download", "upload", "delete"],
};

export const ADMIN_USER_ROLES: readonly AdminUserRole[] = ["admin", "user"];

// ── Users ─────────────────────────────────────────────────────────────

/** D2 (PR-5): the six `builder_user_access` flags. Admins bypass. */
export interface BuilderAccessFlags {
  can_enter: boolean;
  edit_projects: boolean;
  publish: boolean;
  manage_ai: boolean;
  operations: boolean;
  manage_users: boolean;
}

export type BuilderAccessSummary = "full" | "partial" | "none";

export const BUILDER_ACCESS_FLAGS: readonly (keyof BuilderAccessFlags)[] = [
  "can_enter",
  "edit_projects",
  "publish",
  "manage_ai",
  "operations",
  "manage_users",
];

export interface UserAdmin {
  id: string;
  email: string;
  name: string;
  role: string;
  organization_id: string | null;
  organization_name: string | null;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  /** Number of report subscriptions. Server-aggregated. */
  subscription_count: number;
  /** Number of explicit permission overrides on top of role defaults. */
  override_count: number;
  /** D2 (PR-5): summary of the user's builder access. */
  builder_access: BuilderAccessSummary;
  /** Last portal login (portal migration 079), naive UTC; `null` = no
   *  login since the record started. */
  last_login_at: string | null;
  /** Portal logins in the last 30 days. */
  logins_30d: number;
}

export interface PaginatedUsers {
  items: UserAdmin[];
  total: number;
  limit: number;
  offset: number;
  /** The first portal login ever recorded for this customer; `null` =
   *  nobody has logged in since the record started. */
  logins_record_starts_at: string | null;
}

export interface PasswordResetResponse {
  user_id: string;
  password: string;
}

/** The server's cap on `GET /api/builder/admin/users` —
 *  `admin_users.py` `limit: Query(…, le=200)`. A bigger page is a 422. */
export const USERS_PAGE = 200;

/** What happened to an account mail (`AccessMailOut` on the server). */
export interface AccessMail {
  status: "sent" | "failed" | "not_needed";
  error: string | null;
  expires_at: string | null;
}

export const adminUsersApi = {
  list: (
    params: {
      role?: string;
      is_active?: boolean;
      limit?: number;
      offset?: number;
    } = {},
  ) => api.get<PaginatedUsers>("/api/builder/admin/users", { query: params }),

  /** EVERY non-deleted user, paged at the server's cap. A server that
   *  stops short of its own `total`, or a page that repeats a user
   *  (the list moved under the pages), is an error, never a short list. */
  all: async (): Promise<UserAdmin[]> => {
    const out: UserAdmin[] = [];
    const seen = new Set<string>();
    let offset = 0;
    for (;;) {
      const page = await adminUsersApi.list({ limit: USERS_PAGE, offset });
      for (const u of page.items) {
        if (seen.has(u.id)) {
          throw new Error(
            `users: user ${u.id} came back twice while paging (offset ${offset}); ` +
              "the list changed between pages — reload and try again",
          );
        }
        seen.add(u.id);
        out.push(u);
      }
      if (out.length >= page.total) return out;
      if (page.items.length === 0) {
        throw new Error(
          `users: the server reports ${page.total} user(s) but stopped ` +
            `returning them after ${out.length}`,
        );
      }
      offset += page.items.length;
    }
  },

  /**
   * Without `password` the person is mailed an invitation from
   * noreply@omnitok.com to choose their own (2026-11-03); `mail` says
   * whether it left. With `password` nothing is sent.
   */
  create: (data: {
    email: string;
    name: string;
    password?: string;
    locale?: "en" | "es" | "pt";
    role?: AdminUserRole;
    organization_id?: string | null;
    /** Report keys to subscribe the new user to in the same transaction. */
    initial_subscriptions?: string[];
  }) => api.post<UserAdmin & { mail: AccessMail }>("/api/builder/admin/users", data),

  /** Mail the invitation again; the earlier link stops working. */
  resendInvitation: (id: string) =>
    api.post<AccessMail>(`/api/builder/admin/users/${id}/invitation`),

  /** Mail a link to choose a new password; the current one keeps
   *  working until the person uses it. */
  sendResetLink: (id: string) =>
    api.post<AccessMail>(`/api/builder/admin/users/${id}/reset-link`),

  get: (id: string) => api.get<UserAdmin>(`/api/builder/admin/users/${id}`),

  update: (
    id: string,
    data: {
      name?: string;
      role?: string;
      organization_id?: string | null;
      is_active?: boolean;
    },
  ) => api.put<UserAdmin>(`/api/builder/admin/users/${id}`, data),

  /** Soft delete (`is_deleted=true`) — 204. */
  remove: (id: string) =>
    api.delete<void>(`/api/builder/admin/users/${id}`),

  /**
   * Generate a new random password for the user. The plaintext is
   * returned **once** in the response so the admin can copy it — the
   * builder has no SMTP to email it. Closing the modal without
   * copying loses the password.
   */
  resetPassword: (id: string) =>
    api.post<PasswordResetResponse>(
      `/api/builder/admin/users/${id}/reset-password`,
    ),

  /** D2 (PR-5): read the user's builder access flags (admin → all true). */
  getBuilderAccess: (id: string) =>
    api.get<BuilderAccessFlags>(
      `/api/builder/admin/users/${id}/builder-access`,
    ),

  /** D2 (PR-5): replace all six flags. 409 for admins (they bypass). */
  putBuilderAccess: (id: string, flags: BuilderAccessFlags) =>
    api.put<BuilderAccessFlags>(
      `/api/builder/admin/users/${id}/builder-access`,
      flags,
    ),
};

// ── Permissions matrix ────────────────────────────────────────────────

export interface PermissionOverride {
  report_key: string;
  section: string;
  action: string;
  granted: boolean;
  updated_at: string;
}

/** D1 (PR-5): maestros/files granted at PROJECT level. */
export interface ProjectSectionPermission {
  project_slug: string;
  section: string;
  action: string;
  granted: boolean;
  updated_at: string;
}

export type ProjectPermissionSection = "maestros" | "files";

export interface ProjectSectionPermissionInput {
  project_slug: string;
  section: ProjectPermissionSection;
  action: PermissionAction;
  granted: boolean;
}

export interface ProjectSectionPermissionKey {
  project_slug: string;
  section: ProjectPermissionSection;
  action: PermissionAction;
}

export interface UserPermissions {
  user_id: string;
  role: string;
  subscriptions: string[];
  overrides: PermissionOverride[];
  /** D1 (PR-5): project-level maestros/files rows. */
  project_permissions: ProjectSectionPermission[];
}

export interface PermissionOverrideInput {
  report_key: string;
  section: PermissionSection;
  action: PermissionAction;
  granted: boolean;
}

export interface PermissionOverrideKey {
  report_key: string;
  section: PermissionSection;
  action: PermissionAction;
}

export interface RoleDefault {
  role: string;
  section: string;
  action: string;
  granted: boolean;
  updated_at: string;
}

export interface RoleDefaultInput {
  role: AdminUserRole;
  section: PermissionSection;
  action: PermissionAction;
  granted: boolean;
}

export interface ProjectAccessUser {
  id: string;
  name: string;
  email: string;
  role: string;
  subscribed_keys: string[];
  override_count: number;
}

export interface ProjectAccessResponse {
  project_slug: string;
  report_keys: string[];
  users: ProjectAccessUser[];
}

export const adminPermissionsApi = {
  getUserPermissions: (userId: string) =>
    api.get<UserPermissions>(
      `/api/builder/admin/users/${userId}/permissions`,
    ),

  /** Replace the user's full subscription set. Returns the new set. */
  setSubscriptions: (userId: string, reportKeys: string[]) =>
    api.put<string[]>(
      `/api/builder/admin/users/${userId}/permissions/subscriptions`,
      { report_keys: reportKeys },
    ),

  upsertOverrides: (userId: string, overrides: PermissionOverrideInput[]) =>
    api.put<PermissionOverride[]>(
      `/api/builder/admin/users/${userId}/permissions/overrides`,
      overrides,
    ),

  /** DELETE with a JSON body — the list of override triplets to remove. */
  deleteOverrides: (userId: string, keys: PermissionOverrideKey[]) =>
    api.delete<void>(
      `/api/builder/admin/users/${userId}/permissions/overrides`,
      keys,
    ),

  /**
   * D1 (PR-5): bulk upsert of PROJECT-level maestros/files cells. The
   * backend fans each cell out to the per-report rows the portal
   * enforces (same transaction).
   */
  upsertProjectPermissions: (
    userId: string,
    cells: ProjectSectionPermissionInput[],
  ) =>
    api.put<ProjectSectionPermission[]>(
      `/api/builder/admin/users/${userId}/permissions/project-permissions`,
      cells,
    ),

  /** D1 (PR-5): bulk delete — cells fall back to the role default. */
  deleteProjectPermissions: (
    userId: string,
    keys: ProjectSectionPermissionKey[],
  ) =>
    api.delete<void>(
      `/api/builder/admin/users/${userId}/permissions/project-permissions`,
      keys,
    ),

  listRoleDefaults: () =>
    api.get<RoleDefault[]>("/api/builder/admin/role-defaults"),

  updateRoleDefaults: (updates: RoleDefaultInput[]) =>
    api.put<RoleDefault[]>("/api/builder/admin/role-defaults", updates),

  /**
   * Copy ``source`` user's subscriptions and overrides onto ``target``.
   * Replaces the target's existing matrix entirely. Roles are NOT
   * copied — only the per-cell decisions.
   */
  cloneFrom: (targetUserId: string, sourceUserId: string) =>
    api.post<UserPermissions>(
      `/api/builder/admin/users/${encodeURIComponent(targetUserId)}/clone-permissions/${encodeURIComponent(sourceUserId)}`,
    ),

  /** Inverted matrix view: who can see this project's reports. */
  getProjectAccess: (slug: string) =>
    api.get<ProjectAccessResponse>(
      `/api/builder/admin/projects/${encodeURIComponent(slug)}/access`,
    ),
};
